import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Bell, BellRing, Loader2, X, MapPin, UserCheck, Bookmark } from "lucide-react";
import { toast } from "sonner";

/**
 * My alerts — list of category follows.
 * Backend: GET /classifieds/follows
 * Delete: DELETE /classifieds/follows/{id}
 */
export default function MyAlertsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["my-follows", user?.id],
    queryFn: async () => {
      const [followsRes, savedRes] = await Promise.all([
        sharedApi.get("/classifieds/follows"),
        sharedApi.get("/classifieds/saved-searches").catch(() => ({ data: {} })),
      ]);
      const follows = followsRes.data?.follows || [];
      const saved = (savedRes.data?.saved_searches || []).map((s) => ({
        id: s.id,
        _kind: "saved_search",
        q: s.q,
        category_slug: s.category_slug,
        category_name: s.category_name,
        region: s.region,
        city: s.city,
        min_price: s.min_price,
        max_price: s.max_price,
        name: s.name,
      }));
      return [...follows, ...saved];
    },
    enabled: !!user,
    staleTime: 60_000,
    retry: 0,
  });

  const unfollowMut = useMutation({
    mutationFn: async (row) => {
      if (row._kind === "saved_search") {
        return sharedApi.delete(`/classifieds/saved-searches/${row.id}`);
      }
      return sharedApi.delete(`/classifieds/follows/${row.id}`);
    },
    onSuccess: (_res, row) => {
      qc.invalidateQueries({ queryKey: ["my-follows", user?.id] });
      qc.invalidateQueries({ queryKey: ["saved-searches", user?.id] });
      const label = row.name || row.seller_name || row.category_name || row.q || "this alert";
      toast.success(`Alerts stopped for ${label}.`);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not remove — please try again."),
  });

  const follows = q.data || [];

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="My alerts" description="Sellers and categories you follow — get emailed when new ads go live." />
      <Breadcrumbs items={[{ label: "My alerts" }]} />

      <div className="mt-6">
        <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">My alerts</h1>
        <p className="mt-2 text-slate-500">
          You&apos;ll get an email whenever a seller or category you follow has a new ad go live.
        </p>
      </div>

      {q.isLoading && (
        <div className="mt-10 flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!q.isLoading && q.isError && (
        <div
          data-testid="my-alerts-error"
          className="mt-10 rounded-2xl bg-white shadow-card p-10 text-center"
        >
          <div className="w-12 h-12 mx-auto rounded-full bg-red-50 text-red-600 grid place-items-center">
            <Bell className="w-5 h-5" />
          </div>
          <h2 className="mt-4 font-display text-lg font-bold text-slate-900">
            Couldn&apos;t load your alerts
          </h2>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            We couldn&apos;t reach the alerts service. Please try again.
          </p>
          <button
            type="button"
            onClick={() => q.refetch()}
            data-testid="my-alerts-retry"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-sm font-semibold px-5 py-2 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!q.isLoading && !q.isError && follows.length === 0 && (
        <div
          data-testid="my-alerts-empty"
          className="mt-10 rounded-2xl bg-white shadow-card p-12 text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center">
            <Bell className="w-6 h-6" />
          </div>
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">No alerts yet</h2>
          <p className="mt-2 text-slate-500 max-w-md mx-auto">
            Follow a seller or category to get an email whenever a new ad goes live.
          </p>
          <Link
            to="/"
            data-testid="my-alerts-browse"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2.5 transition-colors"
          >
            Browse categories
          </Link>
        </div>
      )}

      {!q.isLoading && !q.isError && follows.length > 0 && (
        <div className="mt-8 space-y-3" data-testid="my-alerts-list">
          {follows.map((f) => {
            const isSaved = f._kind === "saved_search";
            const isSeller = !!f.seller_id;
            const name = isSaved
              ? (f.name || f.q || f.category_name || "Saved search")
              : isSeller
              ? (f.seller_name || "Seller")
              : (f.category_name || f.category_slug);
            const linkTo = isSaved
              ? `/search?${new URLSearchParams(Object.fromEntries(Object.entries({ q: f.q, category_slug: f.category_slug, region: f.region, city: f.city, min_price: f.min_price, max_price: f.max_price }).filter(([, v]) => v != null && v !== ""))).toString()}`
              : isSeller ? `/seller/${f.seller_id}` : `/c/${f.category_slug}`;
            const iconBg = isSaved
              ? "bg-emerald-50 text-emerald-600"
              : isSeller
              ? "bg-[color:var(--brand-orange)]/10 text-[color:var(--brand-orange)]"
              : "bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)]";
            const IconCmp = isSaved ? Bookmark : isSeller ? UserCheck : BellRing;
            const subtitle = isSaved
              ? [f.category_name || f.category_slug, f.region || f.city, f.min_price != null || f.max_price != null ? `$${f.min_price ?? 0}–$${f.max_price ?? "∞"}` : null].filter(Boolean).join(" · ") || "Any listing"
              : isSeller
              ? "Seller · new-ad alerts"
              : (f.region || "All of NZ");
            return (
              <div
                key={f.id}
                className="rounded-2xl bg-white shadow-card p-4 md:p-5 flex items-center justify-between gap-4"
                data-testid={`my-alert-${f.id}`}
                data-alert-type={isSaved ? "saved_search" : isSeller ? "seller" : "category"}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${iconBg}`} aria-hidden="true">
                    <IconCmp className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <Link
                      to={linkTo}
                      className="block font-display font-bold text-slate-900 hover:text-[color:var(--brand-blue)] transition-colors truncate"
                      data-testid={`my-alert-name-${f.id}`}
                    >
                      {name}
                    </Link>
                    <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-slate-500">
                      {!isSaved && !isSeller && <MapPin className="w-3 h-3" />}
                      {subtitle}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => unfollowMut.mutate(f)}
                  disabled={unfollowMut.isPending}
                  data-testid={`my-alert-unfollow-${f.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                >
                  <X className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
